import { createPortal } from "react-dom";
import React, { useEffect, useRef, useState } from "react";
import { AiCredits, buyCreditPack, fetchAiCredits, formatCredits } from "./aiCredits";
import { useSheetFocus } from "./useSheetFocus";

const ROW_LABEL: Record<string, string> = {
  STARTER: "Free starter credits",
  PURCHASE: "Credits bought",
  ADMIN_GRANT: "Added by HastKala",
  USAGE: "Description written",
};

type Props = {
  open: boolean;
  onClose: () => void;
  /** Called with the fresh balance after a purchase. */
  onChanged: (credits: AiCredits | null) => void;
};

/** "AI credits": balance, packs (existing Razorpay checkout) and the latest use. 1 credit = ₹1. */
const AiCreditsSheet = ({ open, onClose, onChanged }: Props) => {
  const [credits, setCredits] = useState<AiCredits | null>(null);
  const [busy, setBusy] = useState<string>("");
  const [message, setMessage] = useState("");
  const sheetRef = useRef<HTMLDivElement>(null);
  useSheetFocus(open, sheetRef, onClose);

  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  useEffect(() => {
    if (!open) return;
    setMessage("");
    fetchAiCredits().then((fresh) => {
      setCredits(fresh);
      // A grant or a purchase in another tab may have refilled the balance: tell Write from photos too.
      if (fresh) onChangedRef.current(fresh);
    });
  }, [open]);

  if (!open) return null;

  const buy = async (packId: string) => {
    const pack = credits?.packs.find((p) => p.id === packId);
    if (!pack || busy) return;
    setBusy(packId);
    setMessage("");
    try {
      const error = await buyCreditPack(pack);
      const fresh = await fetchAiCredits();
      setCredits(fresh);
      onChanged(fresh);
      setMessage(error || `Added ${formatCredits(pack.pricePaise)} of credits.`);
    } catch {
      setMessage("Payment couldn’t open. Check your connection and try again.");
    } finally {
      setBusy("");
    }
  };

  // Portal to <body>: a transformed page wrapper would otherwise trap position: fixed.
  return createPortal(
    <div className="ps-shell ps-sheet-scrim" role="presentation" onClick={onClose}>
      <div
        ref={sheetRef}
        className="ps-sheet"
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="ai-credits-title"
        onClick={(e) => e.stopPropagation()}>
        <div className="ps-sheet__handle" />
        <h2 id="ai-credits-title">AI credits</h2>
        <p className="ps-credits__balance">
          <b>{credits ? formatCredits(credits.balancePaise) : "…"}</b>
          <span>balance · 1 credit = ₹1</span>
        </p>
        <p style={{ fontSize: 13, color: "#6e6e73", margin: "0 0 13px" }}>
          Writing a description from photos costs about{" "}
          {credits?.estimatePaise?.product_description != null
            ? formatCredits(credits.estimatePaise.product_description)
            : "₹0.20"}{" "}
          each. You can go a little below zero (to {credits ? formatCredits(-Math.abs(credits.overdraftPaise)) : "−₹10"}) so
          work isn’t interrupted.
        </p>
        <div className="ps-credits__packs">
          {(credits?.packs || []).map((pack) => (
            <button
              key={pack.id}
              type="button"
              className="ps-btn ps-btn--line ps-btn--wide"
              disabled={Boolean(busy)}
              onClick={() => buy(pack.id)}
            >
              <span>{busy === pack.id ? "Opening payment…" : `Buy ${formatCredits(pack.pricePaise)}`}</span>
            </button>
          ))}
          {credits && !credits.packs.length ? (
            <p style={{ fontSize: 13, color: "#6e6e73" }}>Packs aren’t on sale yet — ask HastKala to add credits.</p>
          ) : null}
        </div>
        {message ? (
          <p className="ps-note" role="status" style={{ marginTop: 13 }}>
            {message}
          </p>
        ) : null}
        {credits?.recent?.length ? (
          <>
            <div className="ps-label" style={{ marginTop: 16 }}>
              <span>Recent</span>
            </div>
            <ul className="ps-credits__rows">
              {credits.recent.slice(0, 8).map((row, i) => (
                <li key={`${row.createdAt}-${i}`}>
                  <span>
                    {ROW_LABEL[row.type] || row.note || row.type}
                    {row.createdAt ? <small>{new Date(row.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</small> : null}
                  </span>
                  <b className={row.amountPaise < 0 ? "is-neg" : ""}>
                    {row.amountPaise > 0 ? "+" : ""}
                    {formatCredits(row.amountPaise)}
                  </b>
                </li>
              ))}
            </ul>
          </>
        ) : null}
        <div style={{ display: "flex", marginTop: 13 }}>
          <button type="button" className="ps-btn ps-btn--line ps-btn--wide" onClick={onClose}>
            <span>Close</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AiCreditsSheet;
